import { useEffect, useState } from 'react';
import { useAuth } from '../lib/AuthContext.js';
import { ApiClient } from '../lib/api.js';

type Field = 'summary' | 'tone' | 'styleRules' | 'hookPatterns' | 'avoid' | 'promptBlock';

export function Voice() {
  const { getToken } = useAuth();
  const api = new ApiClient(getToken);
  const [profile, setProfile] = useState<any>(null);
  const [savedHandle, setSavedHandle] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [confirmFresh, setConfirmFresh] = useState(false);
  const [lastResult, setLastResult] = useState<{ rescraped: boolean; rescrapedCount: number; topEngagedSample: number; preservedFields: string[]; viaPaste?: boolean; pastedCount?: number } | null>(null);

  useEffect(() => {
    api.voiceCurrent().then(r => setProfile(r.profile)).catch(console.error);
    api.me().then(m => setSavedHandle(m?.user?.xProfile?.handle)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  // Scan via extension: opens @handle in the browser, scrapes their public tweets, imports
  // them, then (re)generates the voice profile.
  async function scanAndGenerate(handle: string, preserveEdits: boolean) {
    setBusy(true); setErr(null); setLastResult(null); setConfirmFresh(false);
    try {
      setProgress('Checking the extension…');
      const { pingExtension, scrapeProfileViaExtension } = await import('../lib/extensionBridge.js');
      const ping = await pingExtension();
      if (!ping.installed) {
        setErr("The tweetvia Chrome extension isn't installed (or isn't signed in). Install it and try again, or paste tweets below.");
        setBusy(false); setProgress(''); return;
      }
      setProgress(`Opening @${handle} in this browser…`);
      const scraped = await scrapeProfileViaExtension(handle);
      if (scraped.blocked) {
        setErr('X is asking you to sign in. Open x.com in this browser, sign in, then try again.');
        setBusy(false); setProgress(''); return;
      }
      if (!scraped.tweets || scraped.tweets.length === 0) {
        setErr("We couldn't find any tweets on that profile. Try a different handle, or paste a few below.");
        setBusy(false); setProgress(''); return;
      }
      setProgress(`Saving ${scraped.tweets.length} tweets…`);
      await api.importScraped({
        handle,
        tweets: scraped.tweets,
        displayName: scraped.displayName,
        bio: scraped.bio,
        profileImageUrl: scraped.profileImageUrl,
        bannerImageUrl: scraped.bannerImageUrl,
        pinnedTweetText: scraped.pinnedTweetText,
        followers: scraped.followers,
        following: scraped.following,
        verified: scraped.verified,
      });
      setSavedHandle(handle);
      await finishGenerate(preserveEdits, { rescraped: true, rescrapedCount: scraped.tweets.length });
    } catch (e: any) { setErr(e.message); setBusy(false); setProgress(''); }
  }

  async function pasteAndGenerate(tweets: string[], preserveEdits: boolean) {
    setBusy(true); setErr(null); setLastResult(null); setConfirmFresh(false);
    try {
      setProgress(`Saving ${tweets.length} pasted tweets…`);
      await api.importPaste(tweets);
      await finishGenerate(preserveEdits, { viaPaste: true, pastedCount: tweets.length });
    } catch (e: any) { setErr(e.message); setBusy(false); setProgress(''); }
  }

  // Re-analyze whatever's already imported, without re-scraping.
  async function regenerateInPlace(preserveEdits: boolean) {
    setBusy(true); setErr(null); setLastResult(null); setConfirmFresh(false);
    await finishGenerate(preserveEdits, {});
  }

  async function finishGenerate(preserveEdits: boolean, extras: { rescraped?: boolean; rescrapedCount?: number; viaPaste?: boolean; pastedCount?: number }) {
    try {
      setProgress('Learning your voice…');
      // Use voiceGenerate when there's no profile yet, regenerate otherwise (preserves edits).
      const result = profile
        ? await api.voiceRegenerate(preserveEdits)
        : await api.voiceGenerate().then(r => ({ profile: r.profile, topEngagedSample: 0, preservedFields: [] as string[] }));
      setProfile(result.profile);
      setLastResult({
        rescraped: !!extras.rescraped,
        rescrapedCount: extras.rescrapedCount ?? 0,
        viaPaste: extras.viaPaste,
        pastedCount: extras.pastedCount,
        topEngagedSample: result.topEngagedSample,
        preservedFields: result.preservedFields,
      });
    } catch (e: any) { setErr(e.message); }
    setBusy(false); setProgress('');
  }

  async function patchField(field: Field, value: any) {
    try {
      const r = await api.voicePatch({ [field]: value });
      setProfile(r.profile);
    } catch (e: any) { setErr(e.message); }
  }

  async function resetField(field: Field) {
    try {
      const r = await api.voiceResetField(field);
      setProfile(r.profile);
    } catch (e: any) { setErr(e.message); }
  }

  const sourceCard = (
    <SourceCard
      savedHandle={savedHandle}
      hasProfile={!!profile}
      busy={busy}
      progress={progress}
      err={err}
      lastResult={lastResult}
      onScan={(h) => scanAndGenerate(h, true)}
      onPaste={(t) => pasteAndGenerate(t, true)}
      onRegenerateInPlace={() => regenerateInPlace(true)}
    />
  );

  if (!profile) {
    return (
      <div className="page">
        <div className="page-head">
          <h1>Voice profile</h1>
          <p className="lede">Give us your X handle or paste a few tweets — we'll learn your tone, style, and vocabulary, then write in your voice.</p>
        </div>
        {sourceCard}
      </div>
    );
  }

  const userEdits: Field[] = profile.userEdits ?? [];
  const wasEdited = (f: Field) => userEdits.includes(f);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Voice profile</h1>
        <p className="lede">
          How tweetvia writes for you. Re-scan your X profile or paste new tweets to update — your edits are preserved.
        </p>
      </div>

      {sourceCard}

      <div className="card subtle">
        <div className="button-row between">
          <div>
            <div className="label">Profile</div>
            <div className="muted small">v{profile.version} · {profile.sourceTweetCount} top-engaged tweets · {profile.model}</div>
          </div>
          {userEdits.length > 0 && (
            <button className="ghost" onClick={() => setConfirmFresh(true)} disabled={busy}>Fresh start — discard my edits</button>
          )}
        </div>
        {confirmFresh && (
          <div className="confirm-row">
            <span>Fresh start will replace your {userEdits.length} edited field{userEdits.length === 1 ? '' : 's'} with AI output (using the tweets already imported). Sure?</span>
            <div className="button-row" style={{ margin: 0 }}>
              <button className="ghost" onClick={() => setConfirmFresh(false)}>Cancel</button>
              <button className="primary" onClick={() => regenerateInPlace(false)} disabled={busy}>Yes, replace</button>
            </div>
          </div>
        )}
      </div>

      <EditableTextField
        field="summary"
        label="Summary"
        value={profile.summary}
        multiline
        edited={wasEdited('summary')}
        onSave={v => patchField('summary', v)}
        onReset={() => resetField('summary')}
      />

      <EditableListField
        field="tone"
        label="Tone"
        helper="Words that describe your overall voice."
        values={profile.tone ?? []}
        edited={wasEdited('tone')}
        onSave={v => patchField('tone', v)}
        onReset={() => resetField('tone')}
        chip
      />

      <EditableListField
        field="styleRules"
        label="Style rules"
        helper="How you construct sentences. These guide every generation."
        values={profile.styleRules ?? []}
        edited={wasEdited('styleRules')}
        onSave={v => patchField('styleRules', v)}
        onReset={() => resetField('styleRules')}
      />

      <EditableListField
        field="hookPatterns"
        label="Hook patterns"
        helper="Recurring opener shapes — the way your tweets start."
        values={profile.hookPatterns ?? []}
        edited={wasEdited('hookPatterns')}
        onSave={v => patchField('hookPatterns', v)}
        onReset={() => resetField('hookPatterns')}
      />

      <EditableListField
        field="avoid"
        label="Avoid"
        helper="Words, phrases, or patterns we should never produce for you."
        values={profile.avoid ?? []}
        edited={wasEdited('avoid')}
        onSave={v => patchField('avoid', v)}
        onReset={() => resetField('avoid')}
      />

      <div className="card">
        <details>
          <summary style={{ cursor: 'pointer', fontWeight: 500, fontSize: 14 }}>Prompt block (advanced)</summary>
          <p className="helper">This is the voice context used for every draft. Edit it directly to fully control how you sound.</p>
          <EditableTextField
            field="promptBlock"
            label=""
            value={profile.promptBlock}
            multiline
            inline
            edited={wasEdited('promptBlock')}
            onSave={v => patchField('promptBlock', v)}
            onReset={() => resetField('promptBlock')}
          />
        </details>
      </div>
    </div>
  );
}

// ----------------------------------------------------------
// Source card — handle scan or paste-tweets, always visible on the Voice page.
// ----------------------------------------------------------
function SourceCard(props: {
  savedHandle?: string;
  hasProfile: boolean;
  busy: boolean;
  progress: string;
  err: string | null;
  lastResult: { rescraped: boolean; rescrapedCount: number; topEngagedSample: number; preservedFields: string[]; viaPaste?: boolean; pastedCount?: number } | null;
  onScan: (handle: string) => void;
  onPaste: (tweets: string[]) => void;
  onRegenerateInPlace: () => void;
}) {
  const [handle, setHandle] = useState(props.savedHandle ?? '');
  const [showPaste, setShowPaste] = useState(false);
  const [pasted, setPasted] = useState('');

  useEffect(() => { if (props.savedHandle && !handle) setHandle(props.savedHandle); /* eslint-disable-next-line */ }, [props.savedHandle]);

  const clean = handle.trim().replace(/^@/, '');
  const validHandle = /^[A-Za-z0-9_]{1,15}$/.test(clean);

  function submitPaste() {
    const tweets = pasted.split(/\n\s*\n+|\n---+\n/).map(s => s.trim()).filter(Boolean);
    if (tweets.length < 3) return;
    props.onPaste(tweets);
  }

  const pastedTweets = pasted.split(/\n\s*\n+|\n---+\n/).map(s => s.trim()).filter(Boolean);

  return (
    <div className="card highlight">
      <div className="label">{props.hasProfile ? 'Update from a new source' : 'Generate your voice'}</div>
      <p className="muted small" style={{ margin: '4px 0 14px' }}>
        Enter your X handle and we'll re-scan your profile via the extension. Or paste a few tweets if you'd rather.
      </p>

      <div className="handle-input-row">
        <span className="handle-prefix">@</span>
        <input
          className="handle-input"
          value={handle}
          onChange={e => setHandle(e.target.value)}
          placeholder="yourhandle"
          onKeyDown={e => { if (e.key === 'Enter' && validHandle && !props.busy) props.onScan(clean); }}
          disabled={props.busy}
        />
        <button
          className="primary"
          onClick={() => props.onScan(clean)}
          disabled={props.busy || !validHandle}
        >
          {props.busy ? (props.progress || 'Working…') : 'Scan and generate'}
        </button>
      </div>

      {!showPaste && (
        <p className="helper" style={{ marginTop: 12 }}>
          <button className="link-button" onClick={() => setShowPaste(true)} disabled={props.busy}>Paste tweets instead</button>
          {props.hasProfile && (
            <>
              {' · '}
              <button className="link-button" onClick={props.onRegenerateInPlace} disabled={props.busy}>
                Re-analyze without re-scraping
              </button>
            </>
          )}
        </p>
      )}

      {showPaste && (
        <div className="paste-fallback" style={{ marginTop: 14 }}>
          <label className="field-label">Paste 3+ tweets (one per line, blank line between)</label>
          <textarea
            value={pasted}
            onChange={e => setPasted(e.target.value)}
            placeholder={'Most people overcomplicate AI agents…\n\nDistribution is a daily habit.\n\nShipping fast is useful. Learning fast is the advantage.'}
            style={{ minHeight: 140 }}
            disabled={props.busy}
          />
          <div className="button-row end" style={{ marginTop: 8 }}>
            <button className="ghost" onClick={() => { setShowPaste(false); setPasted(''); }} disabled={props.busy}>Cancel</button>
            <button className="primary" onClick={submitPaste} disabled={props.busy || pastedTweets.length < 3}>
              {props.busy ? (props.progress || 'Working…') : `Use ${pastedTweets.length || 0} tweets`}
            </button>
          </div>
        </div>
      )}

      {props.lastResult && (
        <div className="status-row status-done" style={{ marginTop: 12 }}>
          <span className="status-icon">✓</span>
          <span>
            {props.lastResult.viaPaste
              ? `Imported ${props.lastResult.pastedCount} pasted tweets. `
              : props.lastResult.rescraped
                ? `Scraped ${props.lastResult.rescrapedCount} fresh tweets via the extension. `
                : 'Re-analyzed your imported tweets. '}
            {props.lastResult.topEngagedSample > 0 && `Used top ${props.lastResult.topEngagedSample} by engagement. `}
            {props.lastResult.preservedFields.length > 0 && `Kept ${props.lastResult.preservedFields.length} of your edits.`}
          </span>
        </div>
      )}
      {props.err && <div className="error" style={{ marginTop: 10 }}>{props.err}</div>}
    </div>
  );
}

// ----------------------------------------------------------
// Editable building blocks
// ----------------------------------------------------------

function EditableTextField(props: {
  field: Field; label: string; value: string; multiline?: boolean; inline?: boolean;
  edited: boolean;
  onSave: (v: string) => void;
  onReset: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(props.value ?? '');

  useEffect(() => { setDraft(props.value ?? ''); }, [props.value]);

  if (!props.label && props.inline) {
    return (
      <>
        {editing ? (
          <>
            <textarea value={draft} onChange={e => setDraft(e.target.value)} style={{ minHeight: 120, fontFamily: 'inherit', marginTop: 8 }} />
            <div className="button-row end" style={{ marginTop: 8 }}>
              <button className="ghost" onClick={() => { setDraft(props.value); setEditing(false); }}>Cancel</button>
              <button className="primary" onClick={() => { props.onSave(draft); setEditing(false); }}>Save</button>
            </div>
          </>
        ) : (
          <>
            <pre style={{ marginTop: 12 }}>{props.value || '(empty)'}</pre>
            <div className="button-row end" style={{ marginTop: 8 }}>
              {props.edited && <button className="ghost" onClick={props.onReset}>Reset to AI</button>}
              <button onClick={() => setEditing(true)}>Edit</button>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <div className="card">
      <div className="button-row between">
        <h3 style={{ margin: 0 }}>
          {props.label} {props.edited && <span className="edited-pill">edited</span>}
        </h3>
        <div className="button-row" style={{ margin: 0 }}>
          {props.edited && !editing && <button className="ghost" onClick={props.onReset}>Reset to AI</button>}
          {!editing && <button onClick={() => setEditing(true)}>Edit</button>}
        </div>
      </div>
      {editing ? (
        <>
          {props.multiline
            ? <textarea value={draft} onChange={e => setDraft(e.target.value)} style={{ minHeight: 80, marginTop: 8 }} />
            : <input value={draft} onChange={e => setDraft(e.target.value)} style={{ marginTop: 8 }} />}
          <div className="button-row end" style={{ marginTop: 8 }}>
            <button className="ghost" onClick={() => { setDraft(props.value); setEditing(false); }}>Cancel</button>
            <button className="primary" onClick={() => { props.onSave(draft); setEditing(false); }}>Save</button>
          </div>
        </>
      ) : (
        <p style={{ margin: 0, color: 'var(--text-dim)', lineHeight: 1.6 }}>{props.value || <span className="muted">(empty)</span>}</p>
      )}
    </div>
  );
}

function EditableListField(props: {
  field: Field; label: string; helper?: string;
  values: string[]; edited: boolean; chip?: boolean;
  onSave: (v: string[]) => void; onReset: () => void;
}) {
  const [items, setItems] = useState<string[]>(props.values);
  const [newItem, setNewItem] = useState('');
  const [editing, setEditing] = useState(false);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');

  useEffect(() => { setItems(props.values); }, [props.values]);

  function add() {
    if (!newItem.trim()) return;
    const next = [...items, newItem.trim()];
    setItems(next); setNewItem('');
    props.onSave(next);
  }
  function remove(i: number) {
    const next = items.filter((_, idx) => idx !== i);
    setItems(next);
    props.onSave(next);
  }
  function startEditing(i: number) { setEditIdx(i); setEditDraft(items[i]); }
  function commitEdit() {
    if (editIdx === null) return;
    const next = items.map((v, i) => i === editIdx ? editDraft : v);
    setItems(next); setEditIdx(null);
    props.onSave(next);
  }

  return (
    <div className="card">
      <div className="button-row between" style={{ marginBottom: 4 }}>
        <h3 style={{ margin: 0 }}>
          {props.label} {props.edited && <span className="edited-pill">edited</span>}
        </h3>
        <div className="button-row" style={{ margin: 0 }}>
          {props.edited && <button className="ghost" onClick={props.onReset}>Reset to AI</button>}
          {!editing && <button onClick={() => setEditing(true)}>Edit</button>}
          {editing && <button onClick={() => setEditing(false)}>Done</button>}
        </div>
      </div>
      {props.helper && <p className="helper" style={{ marginBottom: 10 }}>{props.helper}</p>}

      {props.chip ? (
        <div className="chip-row">
          {items.map((v, i) => (
            <span key={i} className={`chip${editing ? ' removable' : ''}`}>
              {v}
              {editing && <button className="chip-x" onClick={() => remove(i)} aria-label="Remove">×</button>}
            </span>
          ))}
          {editing && (
            <span className="chip-add">
              <input
                value={newItem}
                onChange={e => setNewItem(e.target.value)}
                placeholder="+ add"
                onKeyDown={e => { if (e.key === 'Enter' && newItem.trim()) add(); }}
              />
            </span>
          )}
        </div>
      ) : (
        <ul className="editable-list">
          {items.map((v, i) => (
            <li key={i}>
              {editIdx === i ? (
                <>
                  <input value={editDraft} onChange={e => setEditDraft(e.target.value)} autoFocus onKeyDown={e => { if (e.key === 'Enter') commitEdit(); }} />
                  <button className="ghost" onClick={() => setEditIdx(null)}>Cancel</button>
                  <button className="primary" onClick={commitEdit}>Save</button>
                </>
              ) : (
                <>
                  <span className="item-text">{v}</span>
                  {editing && (
                    <span className="item-actions">
                      <button className="ghost tiny" onClick={() => startEditing(i)}>Edit</button>
                      <button className="ghost tiny" onClick={() => remove(i)}>Remove</button>
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
          {editing && (
            <li className="add-row">
              <input
                value={newItem}
                onChange={e => setNewItem(e.target.value)}
                placeholder="+ Add new"
                onKeyDown={e => { if (e.key === 'Enter' && newItem.trim()) add(); }}
              />
              <button className="primary" onClick={add} disabled={!newItem.trim()}>Add</button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
